import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import BackArrow from "../assets/images/back-arrow.svg";
import RightArrow from "../assets/health-icon/rightArrow.svg";

const DigiMerAssesment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="flex items-center gap-3 px-4 py-4">
        <Link
          to={-1}
          className="w-9 h-9 bg-[#2D2D2D] rounded-full flex items-center justify-center"
        >
          <img src={BackArrow} alt="" className="w-4 h-4" />
        </Link>

        <span className="text-[16px] font-medium text-white">
          Assessment Result
        </span>
      </div>

      <div className="px-4 pt-2 pb-4 flex flex-col items-center">
        <CircularHealthIndicator percentage={48} label="Moderate" />
        <p className="text-[16px] text-[#FFFFFF]">Overall Health Risk Score</p>
      </div>

      <div className="px-4 py-4 space-y-4 flex-1">
        <RiskScoreCard
          title="CVD Risk Score"
          description="A CVD Risk Score is a clinically validated calculation that estimates a person’s likelihood of developing a major cardiovascular event—such as a heart attack or stroke—within a set period (typically 10 years) using key factors like age, sex, blood pressure, cholesterol, smoking status, diabetes, BMI, and family history. It helps identify high-risk individuals early and is widely used in healthcare and insurance through established models such as Framingham, QRISK3, and ASCVD to guide prevention, treatment, and underwriting decisions."
          score={85}
          borderColor="#E25831"
        />

        <RiskScoreCard
          title="Non-CVD Risk Score"
          description="A Non-CVD Risk Score is a composite measure that estimates an individual’s likelihood of developing non-cardiovascular health conditions—such as metabolic disorders, respiratory issues, mental health risks, nutritional deficiencies, lifestyle-related conditions, or general morbidity—based on factors like age, BMI, lifestyle habits, symptoms, medical history, and environmental exposure. It serves as an overall indicator of non-cardiac disease burden, helping insurers and healthcare providers identify individuals who may require additional screening, lifestyle interventions, or medical follow-up beyond cardiovascular risks."
          score={16}
          borderColor="#B7E54C"
        />
      </div>

      <div className="px-4 py-4 pb-8">
        <button
          onClick={() => {
            navigate("/all-combined-result", {
              state: location.state,
            });
          }}
          className="w-full py-3 bg-white text-[15px] text-black rounded-2xl font-semibold flex items-center justify-center gap-2"
        >
          Detailed Result
          <img src={RightArrow} alt="arrow" className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

function CircularHealthIndicator({ percentage, label }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setProgress(percentage);
    }, 300);
    return () => clearTimeout(timer);
  }, [percentage]);

  // radiuses changed for better spacing
  const outerRadius = 70;
  const middleRadius = 55;
  const innerRadius = 40;

  // thinner stroke for clean ring separation
  const stroke = 9;

  const outer = 2 * Math.PI * outerRadius;
  const middle = 2 * Math.PI * middleRadius;
  const inner = 2 * Math.PI * innerRadius;

  return (
    <div className="relative w-60 h-60 flex items-center justify-center">
      <svg
        className="absolute w-full h-full -rotate-[90deg]"
        viewBox="0 0 220 220"
      >
        {/* OUTER YELLOW ARC */}
        <circle
          cx="110"
          cy="110"
          r={outerRadius}
          fill="none"
          stroke="#222"
          strokeWidth={stroke}
        />
        <circle
          cx="110"
          cy="110"
          r={outerRadius}
          fill="none"
          stroke="#E2B631"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={outer}
          strokeDashoffset={outer - (outer * progress) / 100}
          className="transition-all duration-[2000ms] ease-out"
        />

        {/* MIDDLE GREEN ARC */}
        <circle
          cx="110"
          cy="110"
          r={middleRadius}
          fill="none"
          stroke="#222"
          strokeWidth={stroke}
        />
        <circle
          cx="110"
          cy="110"
          r={middleRadius}
          fill="none"
          stroke="#B7E54C"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={middle}
          strokeDashoffset={middle - (middle * progress) / 100}
          className="transition-all duration-[1700ms] ease-out"
        />

        {/* INNER ORANGE ARC */}
        <circle
          cx="110"
          cy="110"
          r={innerRadius}
          fill="none"
          stroke="#222"
          strokeWidth={stroke}
        />
        <circle
          cx="110"
          cy="110"
          r={innerRadius}
          fill="none"
          stroke="#E25831"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={inner}
          strokeDashoffset={inner - (inner * progress) / 100}
          className="transition-all duration-[1400ms] ease-out"
        />
      </svg>

      <div className="absolute flex flex-col items-center">
        <p className="text-[26px] font-bold">{percentage}%</p>
        <p className="text-[11px] text-gray-300">{label}</p>
      </div>
    </div>
  );
}

function RiskScoreCard({ title, description, score, borderColor }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setProgress(score), 300);
    return () => clearTimeout(timer);
  }, [score]);

  return (
    <div
      className={`p-[2px] w-full space-y-4 rounded-lg`}
      style={{
        background: `linear-gradient(to top, ${borderColor}33, ${borderColor})`,
      }}
    >
      <div className="bg-gray-900 rounded-lg p-5">
        <h3 className="text-[20px] font-semibold text-white mb-2">{title}</h3>

        <p className="text-[9px] text-white/70 font-light text-justify leading-tight mb-14">
          {description}
        </p>

        <div className="relative w-full mb-2">
          <div
            className="absolute -top-12 translate-x-[-50%] transition-all duration-[1000ms] ease-out 
                                   text-white font-bold text-[24px] pointer-events-none"
            style={{ left: `${progress}%` }}
          >
            {score}
          </div>

          <div className="h-[12px] rounded-full bg-gradient-to-r from-[#64BD40] via-[#FFC444] to-[#C81616]" />

          <div
            className="absolute top-1/2 -translate-y-1/2 translate-x-[-50%] transition-all duration-[1000ms] ease-out"
            style={{ left: `${progress}%` }}
          >
            <div className="w-8 h-8 bg-[#C7DCF6] rounded-full border-[3px] border-white shadow-md" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DigiMerAssesment;
